import { Grid } from '@mui/material';
import { CustomTabPanel } from 'views/Production/CustomPanel/index';
import ProductiveHours from '../components/ProductiveHours';
import DateRangeDiv from 'views/Utilization/HeaderMenu/DateRangeDiv';
import GridAnalytics from '../components/GridAnalytics';
import { ThemePalette } from 'mui.theme';
import HoursBreakdown from '../components/HoursBreakdown';
import Suggestions from 'views/Utilization/components/Suggestions/Suggestions';
import AssetWrapper from './AssetWrapper';
import { getShiftHours } from '../utils';
import { useAppSelector } from 'store/hook';
import { memo, useMemo } from 'react';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export interface AggregatedResult {
  totalFleet: FuelConsumptionWithPercentage;
  haulers: FuelConsumptionWithPercentage;
  loaders: FuelConsumptionWithPercentage;
}

export interface FuelConsumption {
  totalFuel: number;
  idleFuel: number;
}

export interface FuelConsumptionWithPercentage extends FuelConsumption {
  idleFuelPercentage: number;
}

export function calculateIdleFuelPercentage(
  idleFuel: number,
  totalFuel: number
): number {
  return totalFuel > 0 ? (idleFuel / totalFuel) * 100 : 0;
}

export function aggregateFuelConsumption(data: any[]): AggregatedResult {
  const result: AggregatedResult = {
    totalFleet: { totalFuel: 0, idleFuel: 0, idleFuelPercentage: 0 },
    haulers: { totalFuel: 0, idleFuel: 0, idleFuelPercentage: 0 },
    loaders: { totalFuel: 0, idleFuel: 0, idleFuelPercentage: 0 },
  };

  data.forEach((item) => {
    const totalFuel = parseFloat(item.total_fuel_consumption_l);
    const idleFuel = parseFloat(item.total_idle_fuel_consumption_l);

    // Aggregate for total fleet
    result.totalFleet.totalFuel += totalFuel;
    result.totalFleet.idleFuel += idleFuel;

    // Aggregate separately for haulers and loaders
    if (item.asset_type === 'haul_truck') {
      result.haulers.totalFuel += totalFuel;
      result.haulers.idleFuel += idleFuel;
    } else if (item.asset_type === 'loader') {
      result.loaders.totalFuel += totalFuel;
      result.loaders.idleFuel += idleFuel;
    }
  });

  // Calculate percentages for each group
  result.totalFleet.idleFuelPercentage = calculateIdleFuelPercentage(
    result.totalFleet.idleFuel,
    result.totalFleet.totalFuel
  );
  result.haulers.idleFuelPercentage = calculateIdleFuelPercentage(
    result.haulers.idleFuel,
    result.haulers.totalFuel
  );
  result.loaders.idleFuelPercentage = calculateIdleFuelPercentage(
    result.loaders.idleFuel,
    result.loaders.totalFuel
  );

  return result;
}

interface Props {
  value: number;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  assetOverview: any;
  aggregation: string;
  setAggregation: any;
  shiftNumber: number;
  setShiftNumber: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  isPrinting: boolean;
  utilizationType: string;
  setUtilizationType: any;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  selectedChartSource: any;
  setSelectedChartSource: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const AllFleet = ({
  value,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  assetOverview,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  isPrinting,
  utilizationType,
  setUtilizationType,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  selectedChartSource,
  setSelectedChartSource,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { customerCode } = useCustomerInfo();

  const loadingStates = useAppSelector(
    (state) => state.utilizationReducer.loadingStates
  );

  const dataSummary = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.summary
  );

  const dataAssets = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.assets
  );

  const fleetPeriodSummary = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.fleetPeriodSummary
  );

  const fleetDetails = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.fleetDetails
  );

  const dataScore = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.score
  );

  const dataSummaryChart = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.summaryChart.fleet
  );

  const summaryData = useMemo(() => {
    // if these change (especially the diff values), they should be chnaged in the CSV file generation on the backend as well
    return {
      utilization:
        dataSummary.fleet_avg_utilization_engine_by_shift_hours * 100,
      utilizationDiff:
        dataSummary.fleet_avg_utilization_engine_working_minute_diff,
      idling: dataSummary.fleet_avg_idling_duration,
      idlingDiff: dataSummary.fleet_avg_idling_duration_diff,
      shiftHours: getShiftHours(customerCode) * 60, // Shift hours in mins,
      shiftHoursDiff: 0,
      engineHours: dataSummary.fleet_avg_utilization_engine_working_minute,
      engineHoursDiff:
        dataSummary.fleet_avg_utilization_engine_working_minute_diff,
      engineActiveMinutes:
        dataSummary.fleet_avg_utilization_engine_active_minute,
      engineActiveMinutesDiff:
        dataSummary.fleet_avg_utilization_engine_active_minute_diff,
    };
  }, [dataSummary, customerCode]);

  const aggregatedData = useMemo(
    () => aggregateFuelConsumption(dataAssets),
    [dataAssets]
  );
  const haulerAssets = useMemo(
    () =>
      dataAssets.filter((item) =>
        ['haul_truck', 'light_truck', 'heavy_truck'].includes(item.asset_type)
      ),
    [dataAssets]
  );
  const loaderAssets = useMemo(
    () => dataAssets.filter((item) => ['loader'].includes(item.asset_type)),
    [dataAssets]
  );

  const haulerBreakDown = useMemo(() => {
    return {
      productiveHours: dataSummary.hauler_total_engine_working_minutes,
      productiveHoursDiff: dataSummary.hauler_total_engine_working_minutes_diff,
      idling: dataSummary.hauler_total_idling_minutes,
      idlingDiff: dataSummary.hauler_total_idling_minutes_diff,
      noOfAssets: dataSummary.number_of_haulers,
      dateRange: dataSummary.date_range,
      engineHours: dataSummary.hauler_total_engine_active_minutes,
      shiftHours: dataSummary.hauler_total_shift_hours,
      engineHoursDiff: dataSummary.hauler_total_engine_active_minutes_diff,
      shiftHoursDiff: dataSummary.hauler_total_shift_hours_diff,
    };
  }, [dataSummary]);

  const loaderBreakDown = useMemo(() => {
    return {
      productiveHours: dataSummary.loader_total_engine_working_minutes,
      productiveHoursDiff: dataSummary.loader_total_engine_working_minutes_diff,
      idling: dataSummary.loader_total_idling_minutes,
      idlingDiff: dataSummary.loader_total_idling_minutes_diff,
      noOfAssets: dataSummary.number_of_loaders,
      dateRange: dataSummary.date_range,
      engineHours: dataSummary.loader_total_engine_active_minutes,
      shiftHours: dataSummary.loader_total_shift_hours,
      engineHoursDiff: dataSummary.loader_total_engine_active_minutes_diff,
      shiftHoursDiff: dataSummary.loader_total_shift_hours_diff,
    };
  }, [dataSummary]);

  return (
    <>
      <CustomTabPanel value={value} index={0}>
        <div>
          <ProductiveHours
            chartData={fleetPeriodSummary.fleet}
            detailsData={fleetDetails.fleet}
            assetType="all"
            setDetailsVal={setDetailsVal}
            setUserSelections={setUserSelections}
            selectedChartSource={selectedChartSource}
            setSelectedChartSource={setSelectedChartSource}
          />

          <DateRangeDiv
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            ignoreYtd={value === 2}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            utilizationType={utilizationType}
            setUtilizationType={setUtilizationType}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <GridAnalytics
            isLoading={loadingStates.isLoadingFleetSummary}
            assetOverview={assetOverview}
            summary={summaryData}
            score={dataScore}
            data={dataSummaryChart}
            aggregation={aggregation}
            isSelected={isSelected}
            assetType="total"
            shiftNumber={shiftNumber}
            utilizationType={utilizationType}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setAnalyticsData={setAnalyticsData}
          />

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={9} sm={12} md={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <HoursBreakdown
                  enableDarkTheme={enableDarkTheme}
                  title={t('hauler_hours_breakdown')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  customerCode={customerCode}
                  data={haulerBreakDown}
                  setHoursBreakDown={(value: any) =>
                    setHoursBreakDown((prev: any) => ({
                      ...prev,
                      hauler: value,
                    }))
                  }
                />
                <HoursBreakdown
                  enableDarkTheme={enableDarkTheme}
                  title={t('loaders_hours_breakdown')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  customerCode={customerCode}
                  data={loaderBreakDown}
                  setHoursBreakDown={(value: any) =>
                    setHoursBreakDown((prev: any) => ({
                      ...prev,
                      loader: value,
                    }))
                  }
                />
              </Grid>
            </Grid>
            <Suggestions
              minHeight="400px"
              data={aggregatedData}
              assetType="all"
              setSuggestionData={setSuggestionData}
            />
          </Grid>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <AssetWrapper
              // Updated to use standard translation key for haulers
              title={t('std_asset_haulers')}
              data={haulerAssets}
              utilizationType={utilizationType}
            />
            <AssetWrapper
              // Updated to use standard translation key for loaders
              title={t('std_asset_loaders')}
              data={loaderAssets}
              isLoaderType
              utilizationType={utilizationType}
            />
          </Grid>
        </div>
      </CustomTabPanel>
    </>
  );
};

export default memo(AllFleet);
